import React from 'react';

export type StatusTone = 'success' | 'warning' | 'info' | 'danger' | 'neutral' | 'accent';

interface StatusPillProps {
  tone?: StatusTone;
  children: React.ReactNode;
  dot?: boolean;
  className?: string;
}

const TONE_STYLES: Record<StatusTone, { bg: string; text: string; ring: string; dot: string }> = {
  success: {
    bg: 'bg-emerald-50 dark:bg-emerald-500/10',
    text: 'text-emerald-700 dark:text-emerald-300',
    ring: 'ring-emerald-200/70 dark:ring-emerald-500/30',
    dot: 'bg-emerald-500',
  },
  warning: {
    bg: 'bg-amber-50 dark:bg-amber-500/10',
    text: 'text-amber-800 dark:text-amber-300',
    ring: 'ring-amber-200/70 dark:ring-amber-500/30',
    dot: 'bg-amber-500',
  },
  info: {
    bg: 'bg-sky-50 dark:bg-sky-500/10',
    text: 'text-sky-700 dark:text-sky-300',
    ring: 'ring-sky-200/70 dark:ring-sky-500/30',
    dot: 'bg-sky-500',
  },
  danger: {
    bg: 'bg-rose-50 dark:bg-rose-500/10',
    text: 'text-rose-700 dark:text-rose-300',
    ring: 'ring-rose-200/70 dark:ring-rose-500/30',
    dot: 'bg-rose-500',
  },
  neutral: {
    bg: 'bg-brand-surface-2',
    text: 'text-brand-text-muted',
    ring: 'ring-brand-border',
    dot: 'bg-brand-text-muted',
  },
  accent: {
    bg: 'bg-brand-accent/10',
    text: 'text-brand-primary',
    ring: 'ring-brand-accent/30',
    dot: 'bg-brand-accent',
  },
};

const StatusPill: React.FC<StatusPillProps> = ({ tone = 'neutral', children, dot = false, className = '' }) => {
  const t = TONE_STYLES[tone];
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[11px] font-medium rounded-full ring-1 ring-inset ${t.bg} ${t.text} ${t.ring} ${className}`}
    >
      {dot && <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />}
      {children}
    </span>
  );
};

export default StatusPill;
